import { useContext, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import useFetch, { CachePolicies } from 'use-http'
import useToast from '../../hooks/useToast'
import type { DownloadJobStatusResponse } from '../../interfaces'
import { MLContext } from '../../providers'
import { MediaLibraryActions } from '../../providers/MLProvider'
import { PublicLinksInfoContext } from '../../providers/PublicLinksInfoProvider'

const PollOperations = (): null => {
  const { t } = useTranslation()
  const { request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })
  const [{ operations }, dispatch] = useContext(MLContext)
  const { urlPrefix } = useContext(PublicLinksInfoContext)
  const toast = useToast()

  const updateOperation = async (): Promise<void> => {
    for await (const operation of operations) {
      const res = (await request.get(
        `${urlPrefix}api/v1/download/${operation.jobId}`,
      )) as DownloadJobStatusResponse

      if (request.error) {
        dispatch([
          {
            type: MediaLibraryActions.UPDATE_OPERATION,
            payload: operations.filter(op => op.jobId !== operation.jobId),
          },
        ])
        toast({
          description: t(
            'download_status_failed_description',
            "An error occured in getting processing status of your download request.",
          ),
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
        break
      } else if (res.status === 'Failed') {
        dispatch([
          {
            type: MediaLibraryActions.UPDATE_OPERATION,
            payload: operations.filter(op => op.jobId !== operation.jobId),
          },
        ])
        toast({
          title: t('download_failed_title', 'Download failed'),
          description: t(
            'download_failed_description',
            "An error occured in processing your download request. Please try again after some time.",
          ),
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
        break
      } else if (
        res.status === 'Partially succeeded' ||
        res.status === 'Succeeded'
      ) {
        const link = document.createElement('a')
        link.download = res.name
        link.href = res.storageURL ?? 'ik-download'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        dispatch([
          {
            type: MediaLibraryActions.UPDATE_OPERATION,
            payload: operations.filter(op => op.jobId !== operation.jobId),
          },
        ])
        break
      }
    }
  }

  useEffect(() => {
    const interval = setInterval(() => {
      void updateOperation()
    }, 10000)

    return () => void clearInterval(interval)
  }, [operations])

  return null
}

export default PollOperations
